import React, { useMemo } from "react";
import cx from "classnames";
import { PickSlipCard } from "@/features/pickems/components/pick-slip/PickSlipCard";
import {
  PickSlipMessage,
  type PickSlipMessageVariant,
} from "@/features/pickems/components/pick-slip/PickSlipMessage";
import { PickSlipPick } from "@/features/pickems/components/pick-slip/PickSlipPick";
import {
  isMarketActiveForPick,
  getContestOpenRaceMarkets,
} from "@/features/pickems/util/helpers";
import { ClampAlert } from "@/features/betslip/components/parts/ClampAlert";
import { PickSlipUnauthorizedAlerts } from "@/features/pickems/components/pick-slip/PickSlipUnauthorizedAlerts";
import { Button } from "@picklebet/ui";
import {
  Tab,
  Tabs,
  TabsList,
} from "@/features/shell/components/shared/Tabs/Tabs";
import { ScrollArea } from "@picklebet/ui";
import { PageMessage } from "@picklebet/ui";
import { Clamp } from "@picklebet/ui";
import PickSlipFooter from "@/features/pickems/components/pick-slip/PickSlipFooter";
import { PickSlipNoContest } from "@/features/pickems/components/pick-slip/PickSlipNoContest";
import { isRacingContest } from "@/features/pickems/util/helpers";
import { BetNowCta } from "@/features/pickems/components/pick-slip/BetNowCta/BetNowCta";
import { useIsMobile } from "@/hooks/useDisplay";
import { usePermission } from "@/features/auth/hooks/usePermission";
import { useEntry } from "@/hooks/firestore/betting/useEntries";
import { usePickemsContestWithMarkets } from "@/hooks/firestore/pickems/usePickemsContest";
import { navigate } from "@/utilities/navigate";
import { getRoute, routeParam } from "@/utilities/route";
import { useShellStore } from "@/features/shell/stores/shellStore";
import { usePickslipStore } from "@/features/pickems/stores/pickslipStore";
import type { PickslipPick } from "@/features/pickems/types/PickslipPick";
import type { NullableMaybe } from "types/utilities";
import type { PickemsMarketOutcome } from "@/hooks/firestore/models/pickems";
import { triggerHaptic } from "@/utilities/haptics";

import { ChevronDownIcon } from "@picklebet/ui";
import { TickCircleIcon } from "@picklebet/ui";
import { ReactComponent as EmptyIllustration } from "assets/illustrations/empty-pickems.svg";
import { isPickemsSelection } from "@/features/entries/util/type-guards";

import * as styles from "./PickSlip.module.scss";
import { useNewPicks } from "@/features/pickems/hooks/useNewPicks";

type AlertMessage = {
  type: PickSlipMessageVariant;
  message: string;
  cta?: string;
  ctaCallback?: () => void;
  canStillSubmit?: boolean;
};

const findPickProblem = (
  pick: PickslipPick,
  acceptPick: (pick: PickslipPick) => void,
  marketNotActive: boolean,
  marketPointsChanged: boolean,
  isPointsOnlyChange: boolean,
  isCompetitorScratched: boolean,
  isRaceAbandoned: boolean,
): NullableMaybe<AlertMessage> => {
  if (!pick) {
    return null;
  }

  if (pick.errorMessage) {
    return {
      type: "warning",
      message: pick.errorMessage,
    };
  }

  if (!pick.scheduledStartTime) {
    return {
      type: "warning",
      message: "The match you picked has not been scheduled to play",
    };
  }

  // we want a different message from marketNotActive
  if (isRaceAbandoned) {
    return {
      type: "warning",
      message:
        "This selection is from an abandoned market and won't be included in your score",
    };
  }

  if (marketNotActive) {
    return {
      type: "warning",
      message: "A Market related to this selection is not currently active",
    };
  }

  if (marketPointsChanged) {
    return {
      type: "danger",
      message: "Score Updated",
      cta: "Accept",
      ctaCallback: () => {
        triggerHaptic.click();
        acceptPick(pick);
      },
    };
  }

  if (isPointsOnlyChange) {
    return {
      type: "warning",
      message: "You are updating your points for an existing pick",
      canStillSubmit: true, // other warnings cannot submit
    };
  }

  if (isCompetitorScratched) {
    return {
      type: "warning",
      message: "Runner scratched, please replace your pick",
      canStillSubmit: false,
    };
  }
};

const PickSlipContest = () => {
  const contestId = routeParam(getRoute(), "contest");
  const confirmContestId = usePickslipStore((state) => state.confirmContestId);
  const error = usePickslipStore((state) => state.error);
  const pickAcceptedId = usePickslipStore((state) => state.pickAcceptedId);
  const updatePickslip = usePickslipStore((state) => state.updatePickslip);
  const setError = usePickslipStore((state) => state.setError);
  const acceptPick = usePickslipStore((state) => state.acceptPick);
  const removePick = usePickslipStore((state) => state.removePick);
  const { contest, isLoading } = usePickemsContestWithMarkets(contestId);
  const { entry } = useEntry({ entryId: contestId });
  const safeEntry = contestId ? entry : undefined;
  const picks = usePickslipStore((state) => state.picks);
  const newPicks = useNewPicks(safeEntry);
  const permission = usePermission("submitPickems");
  const canSubmitPickems = permission === "GRANTED";
  const isMobile = useIsMobile();
  const closeDock = useShellStore((state) => state.closeDock);

  const markets = useMemo(() => {
    if (!contest || isLoading) return [];

    return contest.markets?.map((market) => {
      const marketCopy = { ...market };
      marketCopy.outcomes = Object.keys(market.outcomes ?? {}).reduce(
        (acc, outcomeId) => {
          const outcome = market.outcomes[outcomeId];
          const outcomeCopy = { ...outcome };
          const picksCopy = picks?.map((p) => ({ ...p })) ?? [];
          const pick = picksCopy.find(
            (pick: PickslipPick) => pick.outcomeId === outcomeCopy.id,
          );
          if (pick && pick.freshOdds !== outcomeCopy.odds) {
            // if the points change back to the original points before accepting we need to not show the odds changed message
            pick.oddsChanged = outcomeCopy.odds !== pick.lockedOdds;
            pick.freshOdds = outcomeCopy.odds;
            updatePickslip({
              picks: picksCopy,
              // if a user is in the 'confirm changes' state (truthy confirmContestId) and the odds changed,
              // revert the state to 'make changes'
              confirmContestId: confirmContestId ? null : confirmContestId,
            });
          }

          acc[outcomeId] = outcomeCopy;
          return acc;
        },
        {} as Record<string, PickemsMarketOutcome>,
      );
      return marketCopy;
    });
  }, [contest?.markets, confirmContestId, picks]);

  // for a historical contest
  if (!contest) {
    return <PickSlipNoContest />;
  }

  const pickCount = picks?.length ?? 0;
  const newPickCount =
    newPicks?.filter((pick) => {
      return isMarketActiveForPick(contest, pick);
    })?.length ?? 0;

  const bodyChildren: JSX.Element[] = [];

  const existingPickCount =
    (safeEntry?.data?.selections ?? [])?.filter((selection) => {
      const activeMarket =
        isPickemsSelection(selection) &&
        isMarketActiveForPick(contest, selection);
      return (
        activeMarket ||
        (isPickemsSelection(selection) && selection?.marketStatus === "ACTIVE")
      );
    })?.length ?? 0;

  // racing contests, unlike sport can consist of multple events/meetings within
  // one competition and we only use 1 of many markets per event
  const uniqueRacingOpenMarkets = getContestOpenRaceMarkets(contest);
  const uniqueSportingActiveMarkets =
    markets.filter(
      (market) =>
        market.status === "ACTIVE" &&
        Object.keys(market.outcomes ?? {}).length > 0,
    ) ?? [];

  const marketsToCount = isRacingContest(contest)
    ? uniqueRacingOpenMarkets
    : uniqueSportingActiveMarkets;

  const entryOnOpenMarkets = safeEntry?.data.selections.filter(
    (selection) =>
      isPickemsSelection(selection) && selection?.marketStatus === "ACTIVE",
  );

  const activeMarketsCount = marketsToCount.length;

  const remainingCount = Math.max(
    activeMarketsCount - newPickCount - existingPickCount,
    0,
  );

  let canMakePick = canSubmitPickems && pickCount > 0 && remainingCount < 1;

  const picksAccepted = !!pickAcceptedId;

  const globalAlerts: JSX.Element[] = [];

  if (picksAccepted && safeEntry) {
    globalAlerts.push(
      <ClampAlert
        key={`picks-accepted`}
        color={`info`}
        icon={<TickCircleIcon />}
        clamp={
          <Clamp
            className={styles.clickable}
            onClick={() => {
              navigate(
                `/entries/${contest.status === "LIVE" ? "live" : "upcoming"}/`,
              );
              triggerHaptic.click();
            }}
            variant={`info`}
          >
            View
          </Clamp>
        }
      >
        Picks Accepted: P-{contest?.contestNumber ?? null}-{pickAcceptedId}
      </ClampAlert>,
    );
    globalAlerts.push(
      <BetNowCta key="betting-cta" entryId={String(safeEntry.entryId)} />,
    );
  }

  if (error) {
    globalAlerts.push(
      <ClampAlert
        key={`picks-error`}
        color={`danger`}
        clamp={
          <Clamp
            className={styles.clickable}
            onClick={() => setError(null)}
            variant={`danger`}
          >
            Got It
          </Clamp>
        }
      >
        {error}
      </ClampAlert>,
    );
    canMakePick = false;
  }

  if (
    (pickCount > 0 && remainingCount > 0) ||
    (existingPickCount > 0 && remainingCount > 0)
  ) {
    globalAlerts.push(
      <ClampAlert key={`picks-remaining`} color={`warning`}>
        {remainingCount +
          (remainingCount === 1 ? " pick" : " picks") +
          " remaining"}
      </ClampAlert>,
    );
  }

  if (pickCount > 0 || existingPickCount > 0) {
    bodyChildren.push(
      ...picks.map((pick: PickslipPick) => {
        const selection = safeEntry?.data?.selections?.find(
          (s) => s.event.eventId === pick.eventId,
        );
        const market = contest?.markets?.find((x) => x.id === pick.marketId);
        const entryPoints =
          selection && isPickemsSelection(selection)
            ? selection.points
            : pick.odds;
        const isAdjustment = pick.odds !== entryPoints;
        const isPointsOnlyChange =
          (selection &&
            isAdjustment &&
            isPickemsSelection(selection) &&
            selection?.outcomeId === pick.outcomeId) ??
          false;
        const marketNotActive = market?.status !== "ACTIVE";
        const isRaceAbandoned =
          market?.event.attributes.raceStatus === "ABANDONED";

        const outcomes = Object.values(
          market?.outcomes ?? {},
        ) as PickemsMarketOutcome[];
        const competitor = outcomes?.find(
          (outcome) => outcome.competitor?.id === pick?.competitor?.id,
        )?.competitor;

        const problem: NullableMaybe<AlertMessage> = findPickProblem(
          pick,
          acceptPick,
          marketNotActive,
          pick.oddsChanged ?? false,
          isPointsOnlyChange,
          competitor?.scratched ?? false,
          isRaceAbandoned,
        );

        if (
          problem?.type === "danger" ||
          (problem?.type === "warning" && !problem?.canStillSubmit)
        ) {
          canMakePick = false;
          triggerHaptic.error();
        }

        return (
          <div
            key={pick.sourceId + ":" + pick.outcomeId}
            className={styles.cardWrapper}
          >
            <PickSlipCard
              message={
                !canSubmitPickems ? (
                  <PickSlipMessage
                    type={"warning"}
                    message={"You are not permitted to perform this action"}
                  />
                ) : problem ? (
                  <PickSlipMessage
                    type={problem.type || "danger"}
                    message={problem.message}
                    actionText={problem.cta}
                    action={problem.ctaCallback}
                  />
                ) : null
              }
            >
              <PickSlipPick
                selection={pick}
                oddsFormat="points"
                doRemoveSelection={() => {
                  if (!pick.marketId) return;
                  removePick(pick.marketId);
                  triggerHaptic.click();
                }}
                eventId={pick.eventId}
                oddsIncreased={
                  pick.odds && entryPoints ? pick.odds > entryPoints : undefined
                }
                oddsDecreased={
                  pick.odds && entryPoints ? pick.odds < entryPoints : undefined
                }
                isScratched={competitor?.scratched ?? false}
              />
            </PickSlipCard>
          </div>
        );
      }),
    );
  }

  const contentBody = (
    <>
      <PickSlipUnauthorizedAlerts />
      {globalAlerts}
      {bodyChildren}
      {pickCount < 1 && (
        <PageMessage
          className={styles.empty}
          title={"Your Pickslip is Empty"}
          subTitle={
            existingPickCount < 1
              ? "Select and submit your picks to enter the contest"
              : "Swap and submit your picks to update your contest entry"
          }
          image={<EmptyIllustration />}
        >
          {isMobile && canSubmitPickems && (
            <Button size={"md"} onClick={closeDock}>
              View Contest
            </Button>
          )}
        </PageMessage>
      )}
    </>
  );

  return (
    <div className={styles.container}>
      <Tabs
        value="pickems"
        className={cx(styles.tabs, {
          [styles.largeFooter]: pickCount > 0,
        })}
      >
        <TabsList
          containerClassName={styles.tabListContainer}
          widget={
            <div onClick={closeDock} className={styles.close}>
              <ChevronDownIcon />
            </div>
          }
          className={styles.tabList}
        >
          <Tab badge={pickCount.toString() || "0"} value="pickems">
            Pick'ems
          </Tab>
        </TabsList>
        <ScrollArea className={styles.scrollArea} orientation="vertical">
          <div className={styles.content}>{contentBody}</div>
        </ScrollArea>
      </Tabs>
      {pickCount > 0 && (
        <PickSlipFooter
          contest={contest}
          hasMadeAllPicks={entryOnOpenMarkets?.length === activeMarketsCount}
          canMakePick={canMakePick}
          pickCount={pickCount}
          marketCount={activeMarketsCount}
        />
      )}
    </div>
  );
};

export { PickSlipContest };
